import { CONSTRUCTION, NEW_TAB, SUBMENU, type QueryRecord } from '@query/shared';
import type { MenuNestedItem } from '../components/NestedMenu.js';

/** Как открывать пункт меню — три ветки из AppFC.kt:175-182. */
export type OpenMode = 'query' | 'newTab' | 'link';

/**
 * Построение дерева меню — перенос submenuByQueryCreate (AppFC.kt:162-191).
 *
 * Логика сохранена: строки с непустым name — пункты меню, строки с пустым name
 * (или name == 'submenu') — вложенные подменю, их heading содержит список подменю
 * через запятую, который раскрывается рекурсивно.
 *
 * Режим открытия по хвосту heading: «*» — новая вкладка со ссылкой на запрос,
 * «**» — новая вкладка с готовым URL из поля query, иначе — обычная загрузка.
 */
export function buildSubmenus(
  submenus: string[],
  titlesList: QueryRecord[],
  onOpenQuery: (query: QueryRecord, mode: OpenMode) => void,
): MenuNestedItem[] {
  const unique = [...new Set(submenus)].sort(compareCodeUnits);

  return unique.map((submenu) => {
    const items = titlesList
      .slice()
      .sort(
        (a, b) => compareCodeUnits(a.submenu, b.submenu) || compareCodeUnits(a.heading, b.heading),
      )
      .filter((query) => query.submenu === submenu)
      .flatMap((query): MenuNestedItem[] => {
        if (query.name !== '' && query.name !== SUBMENU) {
          return [{ label: query.heading, callback: () => onOpenQuery(query, openMode(query.heading)) }];
        }
        return buildSubmenus(query.heading.split(','), titlesList, onOpenQuery);
      });

    return { label: submenu, items };
  });
}

/**
 * Сравнение строк как в Kotlin: по кодовым единицам UTF-16, а НЕ по правилам локали.
 *
 * ⚠️ `localeCompare` здесь применять нельзя: он сравнивает без учёта регистра,
 * и тогда «Order's feeds» уезжает в конец, после всех «foods …». В оригинале
 * (`compareBy { it.submenu }.thenBy { it.heading }`) работает String.compareTo,
 * где заглавные буквы идут раньше строчных: Feeds All → Order's feeds → foods …
 * Расхождение заметно при сравнении меню с живым эталоном (проверено 01.10.2026).
 */
export function compareCodeUnits(a: string, b: string): number {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

export function openMode(heading: string): OpenMode {
  if (!heading.endsWith(NEW_TAB)) return 'query';
  return heading.endsWith(NEW_TAB + NEW_TAB) ? 'link' : 'newTab';
}

/**
 * Корневое меню — перенос menuItemsCreate (AppFC.kt:406-426).
 * Пункты Service подключаются вызывающей стороной (им нужен доступ к store).
 */
export function buildRootMenu(
  titlesList: QueryRecord[],
  serviceItems: MenuNestedItem[],
  onOpenQuery: (query: QueryRecord, mode: OpenMode) => void,
): MenuNestedItem {
  const topLevel = titlesList
    .filter((query) => query.query === '' && query.name !== SUBMENU)
    .map((query) => query.submenu);

  return {
    label: 'Menu',
    items: [...buildSubmenus(topLevel, titlesList, onOpenQuery), { label: 'Service', items: serviceItems }],
  };
}

export { CONSTRUCTION };
